/**
 * i18n-service.ts — Lightweight, dependency-free localization for EUM web components.
 *
 * Dictionaries are baked into the bundle (statically imported below), so there
 * is no runtime fetch, no central endpoint, and no per-client configuration
 * required. Each component simply calls `i18n.t('namespace.key', params)`.
 *
 * Design:
 *   - `en` is the canonical dictionary and the single source of translation keys.
 *   - Other locales are `DeepPartial<Dictionary>`; missing keys fall back to `en`
 *     at load time, so a partially translated locale still renders completely.
 *   - Locale is resolved once via `resolveLocale()` (explicit override → <html lang>
 *     → path segment → navigator.language → 'en').
 *
 * Usage:
 *   import { i18n } from '../core/i18n/i18n-service.js';
 *   i18n.setLocale(resolveLocale(el, config));
 *   i18n.t('memberDirectory.noResultsFor', { term });
 */

import { en, type Dictionary } from './locales/en.js';
import { fr } from './locales/fr.js';

export type { Dictionary } from './locales/en.js';

/** Recursively marks every property of T optional (for partial locale files). */
export type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

/**
 * Union of every dot-path key in the dictionary (e.g. 'common.close',
 * 'memberDirectory.noResults'). Gives callers compile-time key safety.
 */
export type TranslationKey = DotPaths<Dictionary>;

type DotPaths<T, Prefix extends string = ''> = {
    [K in keyof T & string]: T[K] extends object
        ? DotPaths<T[K], `${Prefix}${K}.`>
        : `${Prefix}${K}`;
}[keyof T & string];

/**
 * Values that can be interpolated into a string via `{name}` placeholders.
 * A placeholder may declare a format hint (`{count:number}`, `{when:date}`,
 * `{total:currency(USD)}`) which routes the value through the browser-native
 * `Intl` APIs using the active locale. Plain `{name}` placeholders are
 * stringified as-is, so existing keys are unaffected.
 */
export type TranslationParams = Record<string, string | number | Date>;

/**
 * Per-instance overrides supplied via component config (e.g. a `translations`
 * object). May be nested like a dictionary or a flat dot-path map — both are
 * accepted and flattened. Values override the active locale for that instance
 * only, so one client can reword a key without affecting others.
 */
export type TranslationOverrides = DeepPartial<Dictionary> | Record<string, string>;

/**
 * A bound translation function. Returned by `i18n.createTranslator()` so each
 * component instance can carry its own config overrides while still sharing the
 * baked-in locale dictionaries.
 */
export type Translator = (key: TranslationKey | string, params?: TranslationParams) => string;

/** Registry of baked-in locales. Add new locales here after authoring them. */
const LOCALES: Record<string, DeepPartial<Dictionary>> = {
    en,
    fr,
};

const DEFAULT_LOCALE = 'en';

/**
 * Shape of a remotely-fetched (or inline) locale pack. Two forms are accepted:
 *
 *   Single locale:  { locale: 'es', extends?: 'en', translations: {...} }
 *   Multiple:       { locales: { es: {...}, de: {...} } }
 *
 * `translations` (and each value under `locales`) may be nested like a
 * dictionary or a flat dot-path map — both are flattened. Use a pack to add a
 * brand-new language not in the bundle, or to override keys of an existing one.
 */
export interface LocalePack {
    locale?: string;
    /** Base locale to layer these strings over (default 'en'). */
    extends?: string;
    translations?: TranslationOverrides;
    locales?: Record<string, TranslationOverrides>;
}

/** Flatten a (possibly nested) dictionary into a dot-path → string map. */
function flatten(obj: Record<string, unknown>, prefix = '', out: Record<string, string> = {}): Record<string, string> {
    for (const [key, value] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (value !== null && typeof value === 'object') {
            flatten(value as Record<string, unknown>, path, out);
        } else if (value !== undefined) {
            out[path] = String(value);
        }
    }
    return out;
}

/**
 * Format a single interpolated value. A placeholder may carry an optional
 * format hint after a colon:
 *
 *   {count}                 → String(value)              (backward-compatible)
 *   {count:number}          → Intl.NumberFormat
 *   {ratio:percent}         → Intl.NumberFormat (percent)
 *   {total:currency(USD)}   → Intl.NumberFormat (currency)
 *   {when:date}             → Intl.DateTimeFormat (date)
 *   {when:time}             → Intl.DateTimeFormat (time)
 *   {when:datetime}         → Intl.DateTimeFormat (date + time)
 *
 * All Intl formatters key off the active locale. Unknown hints or values that
 * can't be coerced fall back to `String(value)` so a template never throws.
 */
function formatValue(value: string | number | Date, hint: string | undefined, locale: string): string {
    if (!hint) return String(value);

    const currencyMatch = /^currency\(([A-Za-z]{3})\)$/.exec(hint);
    const type = currencyMatch ? 'currency' : hint.toLowerCase();

    try {
        switch (type) {
            case 'number':
                return new Intl.NumberFormat(locale).format(Number(value));
            case 'percent':
                return new Intl.NumberFormat(locale, { style: 'percent' }).format(Number(value));
            case 'currency':
                return new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: currencyMatch![1].toUpperCase(),
                }).format(Number(value));
            case 'date':
                return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(toDate(value));
            case 'time':
                return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(toDate(value));
            case 'datetime':
                return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(toDate(value));
            default:
                return String(value);
        }
    } catch {
        return String(value);
    }
}

/** Coerce an interpolation value into a Date for the date/time formatters. */
function toDate(value: string | number | Date): Date {
    return value instanceof Date ? value : new Date(value);
}

/**
 * Substitute `{name}` (and `{name:hint}`) placeholders in a template, applying
 * locale-aware `Intl` formatting for any placeholder that declares a hint.
 */
function interpolate(template: string, params: TranslationParams | undefined, locale: string): string {
    if (!params) return template;
    return template.replace(/\{(\w+)(?::([^}]+))?\}/g, (match, name: string, hint: string | undefined) =>
        name in params ? formatValue(params[name], hint, locale) : match
    );
}

/**
 * Normalize a config-supplied overrides object (nested or flat) into a flat
 * dot-path map. Exported so callers can pre-flatten if desired.
 */
export function flattenTranslations(overrides: TranslationOverrides | null | undefined): Record<string, string> {
    if (!overrides) return {};
    return flatten(overrides as Record<string, unknown>);
}

class I18nService {
    /** Fully-resolved (English-merged) flat map for the active locale. */
    private strings: Record<string, string>;
    /** Flat English map, always available as the ultimate fallback. */
    private readonly fallback: Record<string, string>;
    private currentLocale = DEFAULT_LOCALE;

    /**
     * Locales registered at runtime (via `registerLocale` / `loadLocalePack`),
     * keyed by normalized tag. These extend the baked-in set: an existing key
     * (e.g. 'fr') layers over its bundled dictionary, while a new key (e.g.
     * 'es') introduces a language the bundle never shipped. Each entry records
     * the flat string map plus the base locale it extends.
     */
    private readonly runtimeLocales = new Map<string, { strings: Record<string, string>; base: string }>();

    /**
     * In-flight / resolved locale-pack fetches keyed by URL, so multiple
     * component instances pointing at the same source share a single request.
     */
    private readonly packFetches = new Map<string, Promise<string[]>>();

    constructor() {
        this.fallback = flatten(en as unknown as Record<string, unknown>);
        this.strings = this.fallback;
    }

    /** The active locale tag (normalized, e.g. 'fr'). */
    get locale(): string {
        return this.currentLocale;
    }

    /** True when a dictionary (baked-in or runtime-registered) exists for `locale`. */
    hasLocale(locale: string): boolean {
        const base = String(locale || '').toLowerCase().split('-')[0];
        return base in LOCALES || this.runtimeLocales.has(base);
    }

    /**
     * Normalize a locale tag to a known base language (e.g. 'fr-CA' → 'fr').
     * Runtime-registered locales are recognized in addition to baked-in ones;
     * unknown tags resolve to the default.
     */
    normalize(locale: string): string {
        const base = String(locale || '').toLowerCase().split('-')[0];
        return base in LOCALES || this.runtimeLocales.has(base) ? base : DEFAULT_LOCALE;
    }

    /**
     * Register (or override) a locale at runtime. `overrides` may be nested or
     * a flat dot-path map. If `locale` matches a baked-in tag the strings layer
     * over that dictionary; otherwise a brand-new language is introduced.
     *
     * `base` is the locale whose strings act as the completion layer beneath the
     * overrides (default 'en'), so a partial pack still renders every key.
     * Returns the normalized tag that was registered.
     */
    registerLocale(locale: string, overrides: TranslationOverrides, base = DEFAULT_LOCALE): string {
        const tag = String(locale || '').toLowerCase().split('-')[0];
        if (!tag) return this.currentLocale;
        const baseTag = String(base || DEFAULT_LOCALE).toLowerCase().split('-')[0];
        const existing = tag in LOCALES ? flatten(LOCALES[tag] as Record<string, unknown>) : {};
        this.runtimeLocales.set(tag, {
            strings: { ...existing, ...flattenTranslations(overrides) },
            base: baseTag,
        });
        // If we just changed the active locale's data, rebuild the live map.
        if (tag === this.currentLocale) this.setLocale(tag);
        return tag;
    }

    /**
     * Fetch a locale pack from `url` and register every locale it declares.
     * The fetch is cached by URL, so concurrent instances sharing one source
     * make a single network request and resolve from the same promise.
     * Resolves to the list of normalized locale tags that were registered.
     */
    async loadLocalePack(url: string): Promise<string[]> {
        const cached = this.packFetches.get(url);
        if (cached) return cached;

        const request = (async () => {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch locale pack from ${url}: ${response.status}`);
            }
            const pack = (await response.json()) as LocalePack;
            return this.registerPack(pack);
        })();

        this.packFetches.set(url, request);
        try {
            return await request;
        } catch (error) {
            // Drop the failed entry so a later instance can retry the fetch.
            this.packFetches.delete(url);
            console.error(error);
            return [];
        }
    }

    /**
     * Register an already-parsed locale pack (inline config, no fetch). Accepts
     * the single-locale or multi-locale shape. Returns the registered tags.
     */
    registerPack(pack: LocalePack | null | undefined): string[] {
        if (!pack) return [];
        const tags: string[] = [];
        if (pack.locales) {
            for (const [locale, translations] of Object.entries(pack.locales)) {
                tags.push(this.registerLocale(locale, translations, pack.extends));
            }
        }
        if (pack.locale && pack.translations) {
            tags.push(this.registerLocale(pack.locale, pack.translations, pack.extends));
        }
        return tags;
    }

    /**
     * Switch the active locale. The requested locale's strings are merged over
     * its base (English by default) so any missing keys fall back gracefully.
     * Runtime-registered locales take precedence over baked-in ones. Unknown
     * locales resolve to the default.
     */
    setLocale(locale: string): void {
        const normalized = this.normalize(locale);
        this.currentLocale = normalized;

        const runtime = this.runtimeLocales.get(normalized);
        if (runtime) {
            const baseStrings =
                runtime.base === DEFAULT_LOCALE || !(runtime.base in LOCALES)
                    ? this.fallback
                    : { ...this.fallback, ...flatten(LOCALES[runtime.base] as Record<string, unknown>) };
            this.strings = { ...baseStrings, ...runtime.strings };
            return;
        }

        if (normalized === DEFAULT_LOCALE) {
            this.strings = this.fallback;
            return;
        }
        const localeStrings = flatten(LOCALES[normalized] as Record<string, unknown>);
        this.strings = { ...this.fallback, ...localeStrings };
    }

    /**
     * Resolve a translation. Returns the localized string with `{param}`
     * placeholders substituted. Lookup order: caller `overrides` → active locale
     * → English fallback → the key itself (which surfaces missing keys during
     * development).
     */
    t(key: TranslationKey | string, params?: TranslationParams, overrides?: Record<string, string>): string {
        const template = overrides?.[key] ?? this.strings[key] ?? this.fallback[key] ?? key;
        return interpolate(template, params, this.currentLocale);
    }

    /**
     * Create a translation function bound to per-instance config overrides.
     * Because the singleton is shared across all component instances, overrides
     * must live with the caller (each element has its own config) rather than on
     * the shared active locale. The overrides object is flattened once here.
     *
     *   this.t = i18n.createTranslator(this.config.translations);
     *   this.t('memberDirectory.editModal.save');
     */
    createTranslator(overrides?: TranslationOverrides | null): Translator {
        const flat = flattenTranslations(overrides);
        const hasOverrides = Object.keys(flat).length > 0;
        return (key, params) => this.t(key, params, hasOverrides ? flat : undefined);
    }
}

/** Shared singleton — import and use directly, mirroring `toastService`. */
export const i18n = new I18nService();
